/** Vendors - a place of ADR-0212's menu (Records): Vendors, Accounts and Supplier requests.
 *
 * Three tabs, each a title card, a strip of tiles, one filter line and one table:
 *
 * - **Vendors** - the company's own note of who bills it, with the accounts and meters beneath and
 *   what those meters add up to (`vendors/VendorsTab.tsx`);
 * - **Accounts** - what each group of meters is billed under (`vendors/AccountsTab.tsx`);
 * - **Supplier requests** - what was asked of suppliers and what came back (`vendors/RequestsTab.tsx`).
 *
 * **A vendor is not a supplier** (ADR-0062, ADR-0040): a supplier is another company's own workspace
 * and names never cross. Nothing a document says is linked to a vendor by this place (SEC-05); the
 * link to a document is a person's act, made at review. Nothing is deleted: the old supplier
 * exchange, the register of who bills what and the purchases ledger are reached from here, unchanged,
 * in drawers.
 */

import { useVendorSummary } from "../api/vendorSummary";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import { useNodeBreakdown } from "./tableKit";
import { useScreen } from "./useScreen";
import { useUrlTab } from "./useUrlTab";
import { AccountsTab } from "./vendors/AccountsTab";
import { RequestsTab } from "./vendors/RequestsTab";
import { VendorsTab } from "./vendors/VendorsTab";

const TABS = [
  { key: "vendors", label: "Vendors" },
  { key: "accounts", label: "Accounts" },
  { key: "requests", label: "Supplier requests" },
] as const;

function VendorsBody() {
  const screen = useScreen();
  const [tab] = useUrlTab(TABS);
  // A vendor's tonnage opens the figures of its linked bills, else of its meters (rule 3).
  const summary = useVendorSummary(screen.window?.period_start, screen.window?.period_end, screen.method);
  const breakdown = useNodeBreakdown(screen, (key) => {
    const id = /^vendor-(.+)$/.exec(key)?.[1];
    const row = summary.data?.vendors.find((vendor) => vendor.id === id);
    return row ? { label: row.name, cut: { vendorId: row.id }, headline: row.kgco2e ?? undefined } : undefined;
  });
  return (
    <ScreenFrame screen={screen} title="Vendors" {...breakdown}>
      {tab === "accounts" ? (
        <AccountsTab screen={screen} />
      ) : tab === "requests" ? (
        <RequestsTab />
      ) : (
        <VendorsTab screen={screen} />
      )}
    </ScreenFrame>
  );
}

export function VendorsScreen() {
  return (
    <RequireSession>
      <VendorsBody />
    </RequireSession>
  );
}
